import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSoloStore, { BOT_CONFIGS } from '../stores/soloStore';
import CoinBalance from '../Components/CoinBalance';
import SeoHead from '../Components/SeoHead';
import { BotMascot } from '../Components/Mascot';
import SoloGameOptions from '../Components/SoloGameOptions';

const BOT_KEYS = ['kafi', 'kosi', 'kemi'];

export default function SoloSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { stats } = useSoloStore();

  const initialBot = BOT_KEYS.includes(location.state?.bot) ? location.state.bot : 'kosi';
  const [selectedBot, setSelectedBot] = useState(initialBot);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex flex-col">
      <SeoHead
        title="Play a Category Word Game Solo Against AI | NameIt"
        description="Race Kafi, Kosi, or Kemi through Name, Place, Animal, Thing. Play a quick 5-round game or the full A–Z challenge in your browser."
        path="/solo"
      />
      {/* Header */}
      <header className="bg-gradient-to-br from-orange-50 to-red-50 border-b border-red-100 shadow-sm px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white transition shadow-sm"
          >
            <span className="text-gray-600 text-sm">←</span>
          </button>
          <div>
            <h1 className="text-lg font-black text-red-700 leading-tight">Play Name, Place, Animal, Thing Solo</h1>
            <p className="text-xs text-gray-600">Choose your opponent</p>
          </div>
        </div>
        <CoinBalance size="small" showBuyButton={false} />
      </header>

      <div className="flex-1 px-4 py-5 space-y-5 max-w-md mx-auto w-full">

        {/* Bot selection */}
        <div className="bg-white rounded-3xl shadow-sm border border-red-50 p-4">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
            Choose a Bot
          </p>
          <div className="space-y-2">
            {BOT_KEYS.map((key) => {
              const bot = BOT_CONFIGS[key];
              const botStats = stats[key] || { wins: 0, losses: 0, ties: 0 };
              const isSelected = selectedBot === key;

              return (
                <button
                  key={key}
                  onClick={() => setSelectedBot(key)}
                  className={`w-full text-left rounded-2xl px-4 py-3.5 border-2 transition-all ${
                    isSelected
                      ? 'bg-red-50 border-red-400 shadow-sm'
                      : 'border-transparent bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 flex-shrink-0">
                      <BotMascot bot={key} state={isSelected ? 'won' : 'idle'} size={48} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800 text-sm">{bot.name}</span>
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          // White text is unreadable on the light green and amber chips.
                          style={{ backgroundColor: bot.color, color: bot.tier === 'hard' ? '#fff' : '#111827' }}
                        >
                          {bot.difficultyLabel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{bot.tagline}</p>
                      {bot.rewardOnWin > 0 && (
                        <p className="text-xs font-semibold text-amber-700 mt-0.5">
                          Win (Full Game): +{bot.rewardOnWin} coins
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-500">
                        {botStats.wins}W · {botStats.losses}L · {botStats.ties}T
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-red-50 p-4">
          <SoloGameOptions bot={selectedBot} />
        </div>
      </div>
    </div>
  );
}
